/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';

import {
  AbstractRestService,
  arrayOf,
  emptyOf,
  identity,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';

import { Observable } from 'rxjs';

import { CheckUsageDto } from '../common/check-usage.dto';
import { CheckUsageMapper } from '../common/check-usage.mapper';
import { CostCenterDetailsMapper } from './mappers/cost-center-details.mapper';
import { CostCenterFinderListItemMapper } from './mappers/cost-center-finder-list-item.mapper';
import { CostCenterListItemMapper } from './mappers/cost-center-list-item.mapper';
import { CostCenterDetailsDto } from './model/cost-center-details.dto';
import {
  CostCenterAutocompleteListItemDto,
  CostCenterFilterDto,
  CostCenterFinderListItemDto,
  CostCenterListItemDto,
} from './model/cost-center-list-item.dto';
import { ComponentGridFilter } from '../components';
import { map$ } from '@gh/rx/operators';

@Injectable()
export class CostCenterRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  get(id: ID): Observable<CostCenterDetailsDto> {
    return this.httpGet(`${this.bp}/cost-centers/${id}`, optimisticLockingOf(CostCenterDetailsMapper));
  }

  create(costCenter: CostCenterDetailsDto): Observable<CostCenterDetailsDto> {
    return this.httpPost(`${this.bp}/cost-centers`, costCenter, optimisticLockingOf(CostCenterDetailsMapper));
  }

  update(costCenter: CostCenterDetailsDto): Observable<CostCenterDetailsDto> {
    return this.httpPut(
      `${this.bp}/cost-centers/${costCenter.id}`,
      costCenter,
      optimisticLockingOf(CostCenterDetailsMapper));
  }

  findAllAutocompleteItemsAsDataSource(filter: CostCenterFilterDto): SlicedDataSource<CostCenterAutocompleteListItemDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/cost-centers/list`,
      { ...filter, autocomplete: true },
      identity(),
      arrayOf(identity()));
  }

  findAllAsFinderDataSource(filter: CostCenterFilterDto): SlicedDataSource<CostCenterFinderListItemDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/cost-centers/finder`,
      filter,
      identity(),
      arrayOf(CostCenterFinderListItemMapper));
  }

  findAllAsDataSource(filter: ComponentGridFilter): SlicedDataSource<CostCenterListItemDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/cost-centers/list`,
      { ...filter },
      identity(),
      arrayOf(CostCenterListItemMapper));
  }

  /**
   * Checks if Take Off Param in usage or not
   * @param costCenter
   * @returns {Observable<CheckUsageDto>}
   */
  checkUsage(costCenter: CostCenterListItemDto): Observable<CheckUsageDto> {
    const { id: id, version } = costCenter;
    return this.httpGet(
      `${this.bp}/cost-centers/check-usage/${id}`,
      { version },
      void 0,
      CheckUsageMapper,
    );
  }

  /**
   * Undelete (activate sense) ToP
   * @param costCenter
   * @returns {Observable<TakeOffParamDto>}
   */
  activate(costCenter: CostCenterListItemDto): Observable<CostCenterListItemDto> {
    return this.httpPut(
      `${this.bp}/cost-centers/undelete/${costCenter.id}`,
      void 0,
      emptyOf(optimisticLockingOf(CostCenterListItemMapper)));
  }

  /**
   * Remove (deactivate sense) ToP
   * @param {TakeOffParamDto} costCenter
   * @param {TakeOffParamsCheckUsageDto} checkUsage
   * @returns {Observable<TakeOffParamDto>}
   */
  deactivate(costCenter: CostCenterListItemDto,
             checkUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/cost-centers/${costCenter.id}`,
      checkUsage,
      CheckUsageMapper);
  }

  propagateCostCenter(costCenterId?: ID): Observable<any> {
    return this.httpPut(
      `${this.bp}/cost-centers/${costCenterId}/add-to-hp-and-jobs`,
      void 0,
      optimisticLockingOf(CostCenterListItemMapper));
  }

  bulkDelete(items: any[]): Observable<any> {
    return this.httpDelete(`${this.bp}/cost-centers`, items, identity());
  }

  getCostCenterCodes(searchString: string): Observable<string[]> {
      return this.httpGet(
        `${this.bp}/cost-centers/codes`,
        { searchString },
        void 0,
        identity()
      ).pipe(map$(response => {
        return (response as any).codes as string[];
      }));
    }
}
